import PropTypes from 'prop-types';
import React from 'react';
import * as cssUtils from 'app/utils/cssUtils';
import classNames from 'classnames';
import { range } from 'lodash/fp';
import Tappable from './Tappable';
import useSheet from '../../jss';

const propTypes = {
  currentPage: PropTypes.number,
  lastPage: PropTypes.number,
  numberOfPages: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  sheet: PropTypes.object.isRequired,
};

const defaultProps = {
  currentPage: 1,
  lastPage: 1,
  numberOfPages: 6,
};

function PaginationBox({
  currentPage,
  lastPage,
  numberOfPages,
  onChange,
  sheet: { classes },
}) {
  const start = Math.max(1, currentPage - Math.ceil(numberOfPages / 2));
  const end = Math.min(lastPage, (start - 1) + numberOfPages);

  if (lastPage <= 1) {
    return null;
  }

  return (
    <div className={classes.wrap}>
      <div className={classNames(classes.link, classes.link_previous)}>
        {currentPage > 1 && (
          <Tappable onClick={() => onChange(currentPage - 1)} className={classes.button}>
            <span className={classes.icon}>
              <span className="ion-ios-arrow-back" />
            </span>
          </Tappable>
        )}
      </div>
      <div className={classes.paginationButtons}>
        {range(start, end + 1).map(page => (
          <Tappable
            key={page}
            onClick={() => onChange(page)}
            className={classNames(classes.button, {
              [classes.button__active]: page === currentPage,
            })}
          >
            {page}
          </Tappable>
        ))}
      </div>
      <div className={classNames(classes.link, classes.link_forward)}>
        {currentPage !== lastPage && (
          <Tappable onClick={() => onChange(currentPage + 1)} className={classes.button}>
            <span className={classes.icon}>
              <span className="ion-ios-arrow-forward" />
            </span>
          </Tappable>
        )}
      </div>
    </div>
  );
}

PaginationBox.propTypes = propTypes;
PaginationBox.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    wrap: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 'auto',
      padding: [theme.spacing.large, 0],
    },
    paginationButtons: {
      alignItems: 'center',
      display: 'flex',
      flex: {
        grow: 0,
        shrink: 0,
        basis: 'auto',
      },
      padding: [0, theme.spacing.small],
    },
    link: {
      alignItems: 'center',
      display: 'flex',
      flex: {
        grow: 0,
        shrink: 0,
        basis: 50,
      },
    },
    button: {
      alignItems: 'center',
      borderRadius: theme.borderRadius.small,
      color: theme.color.basis5,
      cursor: 'pointer',
      display: 'flex',
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.demiBold,
      justifyContent: 'center',
      margin: [0, theme.spacing.tiny / 2],
      padding: [theme.spacing.tiny, theme.spacing.small],
      transition: 'color 0.2s',
      ...cssUtils.hover({
        color: theme.color.white,
      }),
    },
    button__active: {
      backgroundColor: theme.color.basis4,
      color: theme.color.white,
      transition: 'color 0.2s, background-color 0.2s',
      ...cssUtils.hover({
        backgroundColor: theme.color.basis4,
        color: theme.color.white,
      }),
    },
    label: {
      margin: [0, theme.spacing.xsmall],
      textTransform: 'uppercase',
    },
    icon: {
      fontSize: theme.fontSize.large,
      lineHeight: 0,
    },
  };
}

export default useSheet(applyStyles)(PaginationBox);
